import React, { FC, useState } from 'react';
import { Button, Card, Dropdown, Menu, Row, Space } from 'antd';
import { EllipsisIcon } from 'app/components/CommonIcons/CommonIcons';
import {
  selectIsConsultant,
  selectUser,
} from 'app/containers/Authorization/slice/selectors';
import RecipeDetails from 'app/containers/Recipe/parts/RecipeDetails/RecipeDetails';
import { IRecipe } from 'app/containers/Recipe/types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTaskSlice } from '../../slice';
import { mapRecipeResoourcesToTaskPlannedResources } from '../../slice/helpers';
import {
  selectAvailablePlannedTaskResources,
  selectTaskDetailsState,
} from '../../slice/selectors';
import { ITaskAvailableResource } from '../../slice/types';
import AddTaskResourceForm from '../AddTaskResourceForm/AddTaskResourceForm';
import TaskPlannedResourcesTable from '../TaskPlannedResourcesTable/TaskPlannedResourcesTable';
import TaskRecipeDrawer from '../TaskRecipeDrawer/TaskRecipeDrawer';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';

interface IProps {
  setTaskRecipeDescriptionAsNote: React.Dispatch<
    React.SetStateAction<string | null>
  >;
}

const CreateOrUpdateTaskResourcesCard: FC<IProps> = ({
  setTaskRecipeDescriptionAsNote,
}) => {
  const { t } = useTranslation();
  const { mobileOrTablet } = useResponsiveQuery();

  const dispatch = useDispatch();

  const { actions } = useTaskSlice();

  const user = useSelector(selectUser);
  const isConsultant = useSelector(selectIsConsultant);

  const [isRecipeDrawerOpen, setIsRecipeDrawerOpen] = useState(false);
  const { recipe: selectedRecipe } = useSelector(selectTaskDetailsState);

  const availablePlannedResources = useSelector(
    selectAvailablePlannedTaskResources,
  );

  const setSelectedRecipe = (recipe: IRecipe | null) => {
    dispatch(actions.setTaskRecipe(recipe));

    if (recipe?.description.length) {
      setTaskRecipeDescriptionAsNote(recipe.description);
    }
  };

  const onFormSubmit = (resource: ITaskAvailableResource, norm: number) => {
    if (resource) {
      const payload = {
        resourceId: resource.id,
        name: resource.name,
        cultureName: resource.cultureVariation?.culture.name,
        cultureId: resource.cultureVariation?.culture.id,
        norm,
        recommendedNorm: resource.recommendedNorm,
        unit: resource.type.unit,
      };

      dispatch(actions.addTaskPlannedResource(payload));
    }
  };

  const onRecipeSelect = (recipe: IRecipe) => {
    setSelectedRecipe(recipe);

    const plannedResources = mapRecipeResoourcesToTaskPlannedResources(
      recipe.resources,
    );

    dispatch(actions.setTaskPlannedResources(plannedResources));

    setIsRecipeDrawerOpen(false);
  };

  const onRemoveRecipe = () => {
    setSelectedRecipe(null);
    dispatch(actions.setTaskPlannedResources([]));
  };

  return (
    <>
      <Card
        bordered={false}
        title={t('Task.CreateOrUpdateTaskResourcesCard.Resources', 'Resources')}
        style={{
          overflow: 'auto',
          maxHeight: mobileOrTablet ? 'unset' : 'calc(100vh - 170px)',
          maxWidth: '100%',
        }}
        bodyStyle={{
          paddingLeft: mobileOrTablet ? 6 : undefined,
          paddingRight: mobileOrTablet ? 6 : undefined,
          height: mobileOrTablet ? undefined : 'calc(100vh - 230px)',
          overflow: 'auto',
        }}
      >
        {selectedRecipe && (
          <div
            style={{
              marginBottom: 20,

              width: '100%',
            }}
          >
            <Row justify="end" style={{ marginBottom: -30 }}>
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu>
                    <Menu.Item onClick={onRemoveRecipe}>
                      {t('Recipe.RemoveRecipe', 'Remove recipe')}
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button type="primary" shape="circle">
                  <EllipsisIcon size={14} />
                </Button>
              </Dropdown>
            </Row>

            <RecipeDetails
              recipe={selectedRecipe}
              withResourceTable={false}
              withTitle={true}
              withDescription={false}
              recommendationCreator={user}
            />
          </div>
        )}

        <TaskPlannedResourcesTable
          loading={false}
          isEditable={true}
          isPrice={isConsultant}
        />

        <Space align="start" size="large">
          <AddTaskResourceForm
            onFormSubmit={onFormSubmit}
            availableResources={availablePlannedResources}
          />
          {isConsultant && (
            <Button
              style={{ marginTop: 42 }}
              onClick={() => setIsRecipeDrawerOpen(true)}
            >
              {t('Task.SelectRecipe', 'Select recipe')}
            </Button>
          )}
        </Space>
      </Card>
      {isConsultant && (
        <TaskRecipeDrawer
          isOpen={isRecipeDrawerOpen}
          onRecipeSelect={onRecipeSelect}
          onClose={() => setIsRecipeDrawerOpen(false)}
        />
      )}
    </>
  );
};

export default CreateOrUpdateTaskResourcesCard;
